import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { Fragment } from 'react'
import ReactMarkdown from 'react-markdown'

const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => (
    <h1 className='text-xl font-bold tracking-tight text-darkBlack'>
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className='text-lg font-semibold tracking-tight text-darkBlack'>
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className='text-base font-semibold text-darkBlack'>{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className='text-sm font-semibold text-darkBlack'>{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className='text-sm font-medium text-darkBlack'>{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className='text-xs font-semibold uppercase tracking-wide text-darkBlack'>
      {children}
    </h6>
  ),
  p: ({ children }) => (
    <p className='text-sm leading-6 text-gray-700'>{children}</p>
  ),
  strong: ({ children }) => (
    <strong className='font-semibold text-darkBlack'>{children}</strong>
  ),
  em: ({ children }) => <em className='italic text-gray-600'>{children}</em>,
  ul: ({ children }) => (
    <ul className='list-outside list-disc space-y-1 pl-4'>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className='list-outside list-decimal space-y-1 pl-4'>{children}</ol>
  ),
  li: ({ children }) => (
    <li className='text-sm leading-6 text-gray-700'>{children}</li>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className='text-primaryBlue underline hover:text-darkBlue'
      target='_blank'
      rel='noopener noreferrer'
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className='border-l-2 border-primaryBlue pl-4 italic text-gray-500'>
      {children}
    </blockquote>
  ),
  pre: ({ children }) => (
    <pre className='font-mono overflow-x-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-xs'>
      {children}
    </pre>
  ),
  code: ({ children }) => (
    <code className='font-mono rounded bg-gray-100 px-1 py-0.5 text-xs text-gray-800'>
      {children}
    </code>
  ),
  hr: () => <hr className='border-gray-200' />
}

const Drawer = ({ open, onClose, title, content, children, backdrop }) => {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as='div' className='relative z-30' onClose={onClose}>
        {backdrop && (
          <TransitionChild
            as={Fragment}
            enter='transition-opacity ease-in-out duration-500'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity ease-in-out duration-500'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-gray-500/75' />
          </TransitionChild>
        )}
        {!backdrop && <div className='fixed inset-0' />}
        <div className='fixed inset-0 overflow-hidden'>
          <div className='absolute inset-0 overflow-hidden'>
            <div className='pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16'>
              <TransitionChild
                as={Fragment}
                enter='transform transition ease-in-out duration-500 sm:duration-700'
                enterFrom='translate-x-full'
                enterTo='translate-x-0'
                leave='transform transition ease-in-out duration-500 sm:duration-700'
                leaveFrom='translate-x-0'
                leaveTo='translate-x-full'
              >
                <DialogPanel className='pointer-events-auto w-screen max-w-md'>
                  <div className='relative flex h-full flex-col overflow-y-auto bg-white py-6 shadow-xl'>
                    <div className='px-4 sm:px-6'>
                      <div className='flex items-start justify-between'>
                        <DialogTitle className='text-base font-semibold text-gray-900'>
                          {title}
                        </DialogTitle>
                        <div className='ml-3 flex h-7 items-center'>
                          <button
                            type='button'
                            onClick={onClose}
                            className='relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                          >
                            <span className='absolute -inset-2.5' />
                            <span className='sr-only'>Close panel</span>
                            <XMarkIcon aria-hidden='true' className='h-6 w-6' />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className='relative mt-6 flex-1 px-4 sm:px-6'>
                      {content && (
                        <div className='space-y-3'>
                          <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                            {content}
                          </ReactMarkdown>
                        </div>
                      )}
                      {children}
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

Drawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
  children: PropTypes.node,
  backdrop: PropTypes.bool
}

Drawer.defaultProps = {
  backdrop: false
}

export default Drawer
